/**
 * Status Indicator Component
 * Displays a visual status indicator with icon, color, and label
 */

import {
  Check,
  Clock,
  AlertTriangle,
  Coffee,
  Package,
  Calendar,
  Play,
  Minus,
  File,
  Rocket,
  Archive,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  Pause,
  HelpCircle,
  LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';

// Icon mapping
const ICON_MAP: Record<string, LucideIcon> = {
  check: Check,
  clock: Clock,
  'alert-triangle': AlertTriangle,
  'alert-circle': AlertCircle,
  coffee: Coffee,
  package: Package,
  calendar: Calendar,
  play: Play,
  pause: Pause,
  minus: Minus,
  file: File,
  rocket: Rocket,
  archive: Archive,
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'help-circle': HelpCircle,
};

interface StatusIndicatorProps {
  label: string;
  value?: unknown;
  color?: string;
  icon?: string;
  showLabel?: boolean;
  showValue?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'badge' | 'dot' | 'pill';
}

export function StatusIndicator({
  label,
  value,
  color = '#9ca3af',
  icon = 'minus',
  showLabel = true,
  showValue = false,
  size = 'md',
  variant = 'badge',
}: StatusIndicatorProps) {
  const IconComponent = ICON_MAP[icon] || Minus;

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 16,
  };

  if (variant === 'dot') {
    return (
      <div className="flex items-center gap-2" title={`${label}: ${value}`}>
        <span
          className={clsx('rounded-full', {
            'w-2 h-2': size === 'sm',
            'w-3 h-3': size === 'md',
            'w-4 h-4': size === 'lg',
          })}
          style={{ backgroundColor: color }}
        />
        {showLabel && <span className="text-gray-700">{label}</span>}
        {showValue && <span className="text-gray-500">({String(value)})</span>}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'inline-flex items-center gap-1 rounded-full font-medium',
        sizeClasses[size],
        variant === 'pill' && 'border'
      )}
      style={{
        backgroundColor: `${color}20`,
        color: color,
        borderColor: variant === 'pill' ? color : undefined,
      }}
      title={`${label}: ${value}`}
    >
      <IconComponent size={iconSizes[size]} />
      {showLabel && <span>{label}</span>}
      {showValue && <span className="opacity-75">({String(value)})</span>}
    </div>
  );
}
